// src/components/MaintenanceLists/MaintenanceList.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  Timestamp, 
  deleteDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../../firebase';
import ErrorBanner from '../ErrorBanner';
import Modal from '../Modal';
import './MaintenanceList.css';

// Import Chart.js components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Import our extracted components
import BikeHeader from './BikeHeader';
import BikePhoto from './BikePhoto';
import BikeEditModal from './BikeEditModal';
import MaintenanceModal from './MaintenanceModal';
import ObservationModal from './ObservationModal';
import MaintenanceRecords from './MaintenanceRecords';
import ReportingSection from './ReportingSection';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

/**
 * MaintenanceList component displays and manages a motorcycle's details and maintenance records
 * 
 * @param {Object} props - Component props
 * @param {Object|null} props.user - Current authenticated user object or null if not logged in
 * @param {boolean} props.viewOnly - Whether the bike should be displayed in view-only mode
 * @returns {JSX.Element} Rendered component
 */
const MaintenanceList = ({ user, viewOnly = false }) => {
  const { bikeId } = useParams();
  const navigate = useNavigate();
  
  // Bike state
  const [bike, setBike] = useState(null);
  const [bikeDoc, setBikeDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editedBike, setEditedBike] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  const [isObservationModalOpen, setIsObservationModalOpen] = useState(false);
  const [editingMaintenanceItemId, setEditingMaintenanceItemId] = useState(null);
  const [confirmDeleteMaintenanceId, setConfirmDeleteMaintenanceId] = useState(null);
  
  // Maintenance item states
  const [newMaintenanceItem, setNewMaintenanceItem] = useState({
    date: new Date().toISOString().split('T')[0],
    odometer: '',
    category: 'regular',
    subcategory: 'service',
    description: '',
    cost: '',
    location: '',
    notes: '',
    isDIY: false
  });
  
  // Observation record state
  const [newObservationItem, setNewObservationItem] = useState({
    date: new Date().toISOString().split('T')[0],
    odometer: '',
    description: '',
    severity: 'medium',
    status: 'open',
    notes: ''
  });
  
  // UI states
  const [sortOrder, setSortOrder] = useState('newest');
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [isReportingExpanded, setIsReportingExpanded] = useState(false);

  /**
   * Fetch the bike by its public ID
   */
  useEffect(() => {
    const fetchBike = async () => {
      try {
        // Create a query to find the bike by its publicId
        const q = query(collection(db, "bikes"), where("publicId", "==", bikeId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0];
          const bikeData = { id: docData.id, ...docData.data() };
          
          // Set the bike document reference for future use
          setBikeDoc(docData.ref);
          
          // Set the bike data
          setBike(bikeData);
          
          // Determine ownership
          if (viewOnly) {
            setIsOwner(false);
          } else if (user && bikeData.userId) {
            setIsOwner(user.uid === bikeData.userId);
          } else {
            setIsOwner(false);
          }
        } else {
          console.error("Bike not found");
          navigate('/');
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching bike:", error);
        setError("Failed to load the motorcycle details. Please try again.");
        setLoading(false);
      }
    };
    
    // In view-only mode, we can fetch without a user
    if (bikeId && (user || viewOnly)) {
      fetchBike();
    }
  }, [bikeId, user, navigate, viewOnly]);

  /**
   * Update the bike details in Firestore
   */
  const updateBike = async () => {
    if (!bikeDoc || !isOwner || !editedBike) return;
    
    try {
      const updatedBike = {
        ...editedBike,
        lastEdited: Timestamp.now(),
        purchaseDate: editedBike.purchaseDate ? 
          (editedBike.purchaseDate instanceof Timestamp ? 
            editedBike.purchaseDate : 
            Timestamp.fromDate(new Date(editedBike.purchaseDate))
          ) : null
      };
      
      // Update everything except userId, publicId, created, and maintenanceItems
      const { userId, publicId, created, maintenanceItems, id, ...dataToUpdate } = updatedBike;
      
      await updateDoc(bikeDoc, dataToUpdate);
      
      // Update local state
      setBike({
        ...bike,
        ...dataToUpdate
      });
      
      setIsEditModalOpen(false);
    } catch (error) {
      console.error("Error updating bike:", error);
      setError("Failed to update motorcycle details. Please try again.");
    }
  };

  /**
   * Handle editing a motorcycle
   */
  const handleEditBike = () => {
    // Create a copy for editing, converting Timestamp to string for date inputs
    const bikeForEdit = {
      ...bike,
      purchaseDate: bike.purchaseDate ? 
        (typeof bike.purchaseDate.toDate === 'function' ? 
          bike.purchaseDate.toDate().toISOString().split('T')[0] : 
          bike.purchaseDate
        ) : ''
    };
    
    setEditedBike(bikeForEdit);
    setIsEditModalOpen(true);
  };

  /**
   * Delete the bike
   */
  const deleteBike = async () => {
    if (!bikeDoc || !isOwner) return;
    
    try {
      await deleteDoc(bikeDoc);
      navigate('/');
    } catch (error) {
      console.error("Error deleting bike:", error);
      setError("Failed to delete the motorcycle. Please try again.");
    }
  };

  /**
   * Add a new maintenance item to the bike
   */
  const addMaintenanceItem = async () => {
    if (!bikeDoc || !isOwner) return;
    
    try {
      // Create maintenance item with UUID
      const itemId = crypto.randomUUID();
      const maintenanceItem = {
        id: itemId,
        date: Timestamp.fromDate(new Date(newMaintenanceItem.date)),
        odometer: newMaintenanceItem.odometer ? parseInt(newMaintenanceItem.odometer) : null,
        category: newMaintenanceItem.category,
        subcategory: newMaintenanceItem.subcategory || 'other',
        description: newMaintenanceItem.description,
        cost: newMaintenanceItem.cost ? parseFloat(newMaintenanceItem.cost) : null,
        location: newMaintenanceItem.location,
        notes: newMaintenanceItem.notes,
        isDIY: Boolean(newMaintenanceItem.isDIY),
        created: Timestamp.now()
      };
      
      // Update the bike document with the new maintenance item
      await updateDoc(bikeDoc, {
        maintenanceItems: arrayUnion(maintenanceItem),
        lastEdited: Timestamp.now()
      });
      
      // Update local state
      const updatedBike = {
        ...bike,
        maintenanceItems: [...(bike.maintenanceItems || []), maintenanceItem],
        lastEdited: Timestamp.now()
      };
      
      setBike(updatedBike);
      
      // Reset the form and close the modal
      setNewMaintenanceItem({
        date: new Date().toISOString().split('T')[0],
        odometer: '',
        category: 'regular',
        subcategory: 'service',
        description: '',
        cost: '',
        location: '',
        notes: '',
        isDIY: false
      });
      
      setIsMaintenanceModalOpen(false);
    } catch (error) {
      console.error("Error adding maintenance item:", error);
      setError("Failed to add maintenance record. Please try again.");
    }
  };

  /**
   * Edit an existing maintenance item
   */
  const editMaintenanceItem = async () => {
    if (!bikeDoc || !isOwner || !editingMaintenanceItemId) return;
    
    try {
      // Find the existing item
      const existingItems = bike.maintenanceItems || [];
      const oldItem = existingItems.find(item => item.id === editingMaintenanceItemId);
      
      if (!oldItem) {
        setError("Maintenance record not found. Please try again.");
        return;
      }
      
      // Create updated item
      const updatedItem = {
        ...oldItem,
        date: Timestamp.fromDate(new Date(newMaintenanceItem.date)),
        odometer: newMaintenanceItem.odometer ? parseInt(newMaintenanceItem.odometer) : null,
        category: newMaintenanceItem.category,
        subcategory: newMaintenanceItem.subcategory || 'other',
        description: newMaintenanceItem.description,
        cost: newMaintenanceItem.cost ? parseFloat(newMaintenanceItem.cost) : null,
        location: newMaintenanceItem.location,
        notes: newMaintenanceItem.notes,
        isDIY: Boolean(newMaintenanceItem.isDIY)
      };
      
      // Remove the old item and add the updated one
      await updateDoc(bikeDoc, {
        maintenanceItems: arrayRemove(oldItem)
      });
      
      await updateDoc(bikeDoc, {
        maintenanceItems: arrayUnion(updatedItem),
        lastEdited: Timestamp.now()
      });
      
      // Update local state
      const updatedItems = existingItems.map(item => 
        item.id === editingMaintenanceItemId ? updatedItem : item
      );
      
      setBike({
        ...bike,
        maintenanceItems: updatedItems,
        lastEdited: Timestamp.now()
      });
      
      // Reset and close modal
      setNewMaintenanceItem({
        date: new Date().toISOString().split('T')[0],
        odometer: '',
        category: 'regular',
        subcategory: 'service',
        description: '',
        cost: '',
        location: '',
        notes: '',
        isDIY: false
      });
      
      setEditingMaintenanceItemId(null);
      setIsMaintenanceModalOpen(false);
    } catch (error) {
      console.error("Error updating maintenance item:", error);
      setError("Failed to update maintenance record. Please try again.");
    }
  };

  /**
   * Delete a maintenance item
   */
  const deleteMaintenanceItem = async (itemId) => {
    if (!bikeDoc || !isOwner) return;
    
    try {
      const existingItems = bike.maintenanceItems || [];
      const itemToDelete = existingItems.find(item => item.id === itemId);
      
      if (!itemToDelete) {
        setError("Maintenance record not found. Please try again.");
        return;
      }
      
      // Remove the item from Firestore
      await updateDoc(bikeDoc, {
        maintenanceItems: arrayRemove(itemToDelete),
        lastEdited: Timestamp.now()
      });
      
      // Update local state
      const updatedItems = existingItems.filter(item => item.id !== itemId);
      
      setBike({
        ...bike,
        maintenanceItems: updatedItems,
        lastEdited: Timestamp.now()
      });
      
      setConfirmDeleteMaintenanceId(null);
    } catch (error) {
      console.error("Error deleting maintenance item:", error);
      setError("Failed to delete maintenance record. Please try again.");
    }
  };

  /**
   * Open the modal to edit a maintenance item
   */
  const handleEditMaintenanceItem = (item) => {
    // Format the date for the input field
    const formattedDate = item.date instanceof Timestamp ? 
      item.date.toDate().toISOString().split('T')[0] : 
      new Date(item.date).toISOString().split('T')[0];
    
    if (item.type === 'observation') {
      // Handle observation item
      setNewObservationItem({
        date: formattedDate,
        odometer: item.odometer || '',
        description: item.description || '',
        severity: item.severity || 'medium',
        status: item.status || 'open',
        notes: item.notes || ''
      });
      
      setEditingMaintenanceItemId(item.id);
      setIsObservationModalOpen(true);
    } else {
      // Handle regular maintenance item
      setNewMaintenanceItem({
        date: formattedDate,
        odometer: item.odometer || '',
        category: item.category || 'regular',
        subcategory: item.subcategory || 'other',
        description: item.description || '',
        cost: item.cost !== null ? item.cost.toString() : '',
        location: item.location || '',
        notes: item.notes || '',
        isDIY: Boolean(item.isDIY)
      });
      
      setEditingMaintenanceItemId(item.id);
      setIsMaintenanceModalOpen(true);
    }
  };

  /**
   * Get sorted maintenance items
   */
  const getSortedMaintenanceItems = () => {
    if (!bike || !bike.maintenanceItems || !bike.maintenanceItems.length) return [];
    
    return [...bike.maintenanceItems].sort((a, b) => {
      // Convert dates to comparable format
      const dateA = a.date instanceof Timestamp ? a.date.toDate().getTime() : new Date(a.date).getTime();
      const dateB = b.date instanceof Timestamp ? b.date.toDate().getTime() : new Date(b.date).getTime();
      
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
  };

  /**
   * Add a new observation item to the bike
   */
  const addObservationItem = async () => {
    if (!bikeDoc || !isOwner) return;
    
    try {
      // Create observation item with UUID
      const itemId = crypto.randomUUID();
      const observationItem = {
        id: itemId,
        date: Timestamp.fromDate(new Date(newObservationItem.date)),
        odometer: newObservationItem.odometer ? parseInt(newObservationItem.odometer) : null,
        description: newObservationItem.description,
        severity: newObservationItem.severity,
        status: newObservationItem.status,
        notes: newObservationItem.notes,
        created: Timestamp.now(),
        type: 'observation' // Mark as observation type
      };
      
      // Update the bike document with the new observation item
      await updateDoc(bikeDoc, {
        maintenanceItems: arrayUnion(observationItem),
        lastEdited: Timestamp.now()
      });
      
      // Update local state
      const updatedBike = {
        ...bike,
        maintenanceItems: [...(bike.maintenanceItems || []), observationItem],
        lastEdited: Timestamp.now()
      };
      
      setBike(updatedBike);
      
      // Reset the form and close the modal
      setNewObservationItem({
        date: new Date().toISOString().split('T')[0],
        odometer: '',
        description: '',
        severity: 'medium',
        status: 'open',
        notes: ''
      });
      
      setIsObservationModalOpen(false);
    } catch (error) {
      console.error("Error adding observation item:", error);
      setError("Failed to add observation record. Please try again.");
    }
  };

  /**
   * Edit an existing observation item
   */
  const editObservationItem = async () => {
    if (!bikeDoc || !isOwner || !editingMaintenanceItemId) return;
    
    try {
      // Find the existing item
      const existingItems = bike.maintenanceItems || [];
      const oldItem = existingItems.find(item => item.id === editingMaintenanceItemId);
      
      if (!oldItem) {
        setError("Observation record not found. Please try again.");
        return;
      }
      
      // Create updated item
      const updatedItem = {
        ...oldItem,
        date: Timestamp.fromDate(new Date(newObservationItem.date)),
        odometer: newObservationItem.odometer ? parseInt(newObservationItem.odometer) : null,
        description: newObservationItem.description,
        severity: newObservationItem.severity,
        status: newObservationItem.status,
        notes: newObservationItem.notes
      };
      
      // Remove the old item and add the updated one
      await updateDoc(bikeDoc, {
        maintenanceItems: arrayRemove(oldItem)
      });
      
      await updateDoc(bikeDoc, {
        maintenanceItems: arrayUnion(updatedItem),
        lastEdited: Timestamp.now()
      });
      
      // Update local state
      const updatedItems = existingItems.map(item => 
        item.id === editingMaintenanceItemId ? updatedItem : item
      );
      
      setBike({
        ...bike,
        maintenanceItems: updatedItems,
        lastEdited: Timestamp.now()
      });
      
      // Reset and close modal
      setNewObservationItem({
        date: new Date().toISOString().split('T')[0],
        odometer: '',
        description: '',
        severity: 'medium',
        status: 'open',
        notes: ''
      });
      
      setEditingMaintenanceItemId(null);
      setIsObservationModalOpen(false);
    } catch (error) {
      console.error("Error updating observation item:", error);
      setError("Failed to update observation record. Please try again.");
    }
  };

  if (loading) {
    return <div className="loading">Loading motorcycle details...</div>;
  }

  if (!bike) {
    return <div className="not-found">Motorcycle not found</div>;
  }

  const sortedMaintenanceItems = getSortedMaintenanceItems();

  return (
    <div className="list-container">
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      
      <div className="navigation-section">
        <Button
          variant="secondary"
          onClick={() => navigate('/')}
          >
            ← Back to Dashboard
          </Button>
      </div>

      {/* Bike header section */}
      <BikeHeader 
        bike={bike} 
        isOwner={isOwner} 
        onEditClick={handleEditBike}
      />
      
      {/* Bike photo */}
      <BikePhoto 
        bike={bike} 
        isOwner={isOwner} 
        onEditClick={handleEditBike} 
      />
      
      {/* Reporting and analytics section */}
      <ReportingSection
        bike={bike}
        isExpanded={isReportingExpanded}
        onToggleExpand={() => setIsReportingExpanded(!isReportingExpanded)}
        onEditClick={handleEditBike}
      />
      
      {/* Maintenance records section */}
      <MaintenanceRecords 
        maintenanceItems={sortedMaintenanceItems}
        isOwner={isOwner}
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        onEditItem={handleEditMaintenanceItem}
        onDeleteItem={(itemId) => setConfirmDeleteMaintenanceId(itemId)}
        onAddMaintenanceClick={() => setIsMaintenanceModalOpen(true)}
        onAddObservationClick={() => setIsObservationModalOpen(true)}
        expandedRowId={expandedRowId}
        onToggleExpand={(itemId) => setExpandedRowId(expandedRowId === itemId ? null : itemId)}
      />
      
      {/* Modals */}
      <BikeEditModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        editedBike={editedBike}
        setEditedBike={setEditedBike}
        updateBike={updateBike}
      />
      
      <MaintenanceModal 
        isOpen={isMaintenanceModalOpen}
        onClose={() => {
          setIsMaintenanceModalOpen(false);
          setEditingMaintenanceItemId(null);
        }}
        editingItemId={editingMaintenanceItemId}
        maintenanceItem={newMaintenanceItem}
        setMaintenanceItem={setNewMaintenanceItem}
        addMaintenanceItem={addMaintenanceItem}
        editMaintenanceItem={editMaintenanceItem}
      />
      
      <ObservationModal 
        isOpen={isObservationModalOpen}
        onClose={() => {
          setIsObservationModalOpen(false);
          setEditingMaintenanceItemId(null);
        }}
        editingItemId={editingMaintenanceItemId}
        observationItem={newObservationItem}
        setObservationItem={setNewObservationItem}
        addObservationItem={addObservationItem}
        editObservationItem={editObservationItem}
      />
      
      {/* Confirmation Modal for Delete Bike */}
      <Modal
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        title="Confirm Delete"
      >
        <div className="delete-confirmation">
          <p>Are you sure you want to delete this motorcycle? This action cannot be undone and all maintenance records will be lost.</p>
          <div className="modal-actions">
            <Button 
              variant="secondary" 
              onClick={() => setConfirmDeleteId(null)}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={deleteBike}
            >
              Delete Motorcycle
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Confirmation Modal for Delete Maintenance Item */}
      <Modal
        isOpen={confirmDeleteMaintenanceId !== null}
        onClose={() => setConfirmDeleteMaintenanceId(null)}
        title="Confirm Delete Maintenance Record"
      >
        <div className="delete-confirmation">
          <p>Are you sure you want to delete this maintenance record? This action cannot be undone.</p>
          <div className="modal-actions">
            <Button 
              variant="secondary" 
              onClick={() => setConfirmDeleteMaintenanceId(null)}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={() => deleteMaintenanceItem(confirmDeleteMaintenanceId)}
            >
              Delete Record
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MaintenanceList;